import { Injectable, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LazyLoadEvent } from 'primeng/api';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NotificationNavigationService {
  private pendingCommentsTaskId: number | null = null;
  private routeParamsSubscription: Subscription | null = null;

  constructor() { }

  /**
   * Initialize notification navigation by subscribing to route query parameters
   * @param route - Angular ActivatedRoute service
   * @param onCommentOpen - Callback function when comment should be opened
   */
  initializeNotificationNavigation(
    route: ActivatedRoute, 
    onCommentOpen: (taskId: number) => void
  ): void {
    // Check for query parameters to open specific task comments
    this.routeParamsSubscription = route.queryParams.subscribe(params => {
      if (params['openComments']) {
        this.pendingCommentsTaskId = parseInt(params['openComments'], 10);
        console.log(`Received openComments query param: ${this.pendingCommentsTaskId}`);
        
        // Trigger the callback to handle comment opening
        if (this.pendingCommentsTaskId) {
          onCommentOpen(this.pendingCommentsTaskId);
        }
      }
    });
  }

  /**
   * Check if there's a pending task to open comments for and handle the process
   * @param tasks - Array of current tasks
   * @param searchTerm - Current search term
   * @param openCommentsCallback - Function to open comments for a task
   * @param searchCallback - Function to search for a task by ID
   * @param clearSearchCallback - Function to clear search and reload data
   * @param cdr - ChangeDetectorRef for detecting changes
   */
  checkAndOpenCommentsFromNotification(
    tasks: any[], 
    searchTerm: string,
    openCommentsCallback: (task: any) => void,
    searchCallback: (taskId: number) => void,
    clearSearchCallback: () => void,
    cdr: ChangeDetectorRef
  ): void {
    console.log(`checkAndOpenCommentsFromNotification called - pendingCommentsTaskId: ${this.pendingCommentsTaskId}, tasks count: ${tasks?.length || 0}`);
    console.log('Current search term:', searchTerm);
    
    if (this.pendingCommentsTaskId && tasks && tasks.length > 0) {
      console.log('=== Notification Debug Info ===');
      console.log('Looking for task ID:', this.pendingCommentsTaskId, typeof this.pendingCommentsTaskId);
      console.log('Available tasks:', tasks.length);
      
      // Always search for the task ID when coming from notification to filter the grid
      // This provides a consistent experience whether the task is on current page or not
      if (!searchTerm || searchTerm !== this.pendingCommentsTaskId.toString()) {
        console.log(`Searching for task ${this.pendingCommentsTaskId} to highlight it in the grid`);
        searchCallback(this.pendingCommentsTaskId);
        return; // Let the search complete and then this method will be called again
      }
      
      // If we're already searching for the task, find it in results
      const targetTask = tasks.find(task => {
        if (!task.id || !this.pendingCommentsTaskId) return false;
        return task.id === this.pendingCommentsTaskId || 
               task.id === this.pendingCommentsTaskId.toString() ||
               task.id.toString() === this.pendingCommentsTaskId.toString();
      });
      
      if (targetTask) {
        // Task found, open comments
        console.log(`Opening comments for task ${this.pendingCommentsTaskId}`);
        
        // Open the comments modal with a delay to ensure proper rendering
        setTimeout(() => {
          openCommentsCallback(targetTask);
          console.log(`Comments modal opened for task ${this.pendingCommentsTaskId}`);
          
          // Clear the pending task ID after modal is opened
          setTimeout(() => {
            this.pendingCommentsTaskId = null;
            console.log('Cleared pendingCommentsTaskId');
          }, 200);
        }, 300);
        
      } else {
        // Task not found even after search - it may not exist
        console.log(`Task ${this.pendingCommentsTaskId} not found in search results. Task may not exist.`);
        setTimeout(() => {
          this.pendingCommentsTaskId = null;
          clearSearchCallback();
          console.log('Cleared pendingCommentsTaskId - task not found');
        }, 2000);
      }
    }
  }

  /**
   * Search for a specific task by ID
   * @param taskId - Task ID to search for
   * @param searchTermSetter - Function to set the search term
   * @param loadDataCallback - Function to load tasks data
   * @param lastTableLazyLoadEvent - Last lazy load event for the table
   * @param cdr - ChangeDetectorRef for detecting changes
   */
  searchForTaskById(
    taskId: number,
    searchTermSetter: (term: string) => void,
    loadDataCallback: (event: LazyLoadEvent) => void,
    lastTableLazyLoadEvent: any,
    cdr: ChangeDetectorRef
  ): void {
    console.log(`Searching for task ID: ${taskId}`);
    
    // Set search term to the task ID
    searchTermSetter(taskId.toString());
    
    // Force change detection to update the search input field
    cdr.detectChanges();
    
    // Reload the data with the search term
    if (lastTableLazyLoadEvent) {
      // Reset to first page when searching
      lastTableLazyLoadEvent.first = 0;
      loadDataCallback(lastTableLazyLoadEvent);
    } else {
      // Create a basic lazy load event if none exists
      const defaultEvent: LazyLoadEvent = {
        first: 0,
        rows: 10,
        sortField: undefined,
        sortOrder: undefined,
        filters: {}
      };
      loadDataCallback(defaultEvent);
    }
    
    console.log(`Search initiated for task ${taskId}, search term set to: ${taskId.toString()}`);
  }

  /**
   * Get the current pending comments task ID
   */
  getPendingCommentsTaskId(): number | null {
    return this.pendingCommentsTaskId;
  }

  /**
   * Set the pending comments task ID
   */
  setPendingCommentsTaskId(taskId: number | null): void {
    this.pendingCommentsTaskId = taskId;
  }

  /**
   * Clear the pending comments task ID
   */
  clearPendingCommentsTaskId(): void {
    this.pendingCommentsTaskId = null;
  }

  /**
   * Clean up subscriptions
   */
  destroy(): void {
    if (this.routeParamsSubscription) {
      this.routeParamsSubscription.unsubscribe();
      this.routeParamsSubscription = null;
    }
  }
}
